import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, Flag, CheckCircle2, ListTodo, History, Trash2, Plus, FileUp, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, streamSSE } from "@/lib/api";
import { PageTitle, Badge, Fields, DropZone, inCls, btnPrimary, btnGhost } from "./ui";

const TABS = [["opportunities", "Training Opportunities"], ["knowledge", "Knowledge Base"], ["documents", "Documents"], ["conversations", "Conversations"], ["test", "Test Lab"], ["personality", "Personality & Settings"], ["tasks", "Follow-up Tasks"]];

function ImproveDialog({ data, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (data) setF({ question: data.question || "", answer: "", publish: true }); }, [data]);
  if (!data) return null;
  const save = async () => {
    try {
      await api.post("/admin/ai/improve", { ...f, conversation_id: data.conversation_id, message_index: data.message_index });
      toast.success(f.publish ? "Approved answer published — the AI will use it from now on" : "Saved as draft");
      onSaved?.(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="improve-dialog">
        <DialogHeader><DialogTitle>Improve AI answer</DialogTitle></DialogHeader>
        {data.ai_answer && <div className="max-h-32 overflow-y-auto bg-[color:var(--ib-off)] p-3 text-sm text-black/70"><b>AI said:</b> {data.ai_answer}</div>}
        <label className="text-xs font-bold uppercase text-black/60">Customer question</label>
        <input value={f.question || ""} onChange={(e) => setF({ ...f, question: e.target.value })} className={inCls} data-testid="improve-question" />
        <label className="text-xs font-bold uppercase text-black/60">Your approved answer</label>
        <textarea rows={5} value={f.answer || ""} onChange={(e) => setF({ ...f, answer: e.target.value })} className={inCls} data-testid="improve-answer" placeholder="Write the answer you want the assistant to give…" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.publish} onCheckedChange={(v) => setF({ ...f, publish: v })} data-testid="improve-publish-switch" /> Publish immediately (otherwise saved as draft for testing)</label>
        <button className={btnPrimary} onClick={save} disabled={!f.question || !f.answer} data-testid="improve-save-btn">Save approved answer</button>
      </DialogContent>
    </Dialog>
  );
}

function Opportunities({ onImprove }) {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(7);
  const load = () => api.get("/admin/ai/opportunities", { params: { days } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <p>Loading…</p>;
  return (
    <div className="space-y-6" data-testid="ai-opportunities">
      <div className="flex items-center gap-3 text-sm"><span>{d.conversations} conversations in the last</span><select value={days} onChange={(e) => setDays(+e.target.value)} className="border px-2 py-1" data-testid="opp-days"><option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option></select></div>
      <section className="border border-black/10 bg-white p-5">
        <h3 className="font-display text-lg uppercase">Questions the AI couldn't answer confidently</h3>
        <p className="text-sm text-black/55">Write the right answer once — one click publishes it to the assistant.</p>
        <ul className="mt-3 divide-y">
          {d.unanswered.map((u, i) => (
            <li key={i} className="flex items-start justify-between gap-4 py-3" data-testid={`unanswered-${i}`}>
              <div className="min-w-0"><p className="font-semibold">“{u.question}”</p><p className="line-clamp-2 text-sm text-black/55">AI: {u.ai_answer}</p></div>
              <button className={btnPrimary} onClick={() => onImprove(u)} data-testid={`unanswered-approve-${i}`}><Sparkles size={14} /> Approve an answer</button>
            </li>
          ))}
          {!d.unanswered.length && <li className="py-3 text-sm text-black/50">Nothing waiting — nice work.</li>}
        </ul>
      </section>
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="border border-black/10 bg-white p-5">
          <h3 className="font-display text-lg uppercase">Most frequently asked</h3>
          <ul className="mt-3 space-y-2">{d.frequent.map((f, i) => <li key={i} className="flex items-center justify-between gap-3 text-sm"><span>{f.question}</span><span className="flex items-center gap-2"><b>{f.count}×</b><button className="text-xs font-semibold text-[color:var(--ib-orange)]" onClick={() => onImprove({ question: f.question })}>Add answer</button></span></li>)}</ul>
          {!d.frequent.length && <p className="text-sm text-black/50">No questions yet.</p>}
        </section>
        <section className="border border-black/10 bg-white p-5">
          <h3 className="font-display text-lg uppercase">Conversations that generated leads</h3>
          <ul className="mt-3 space-y-2">{d.lead_conversations.map((c) => <li key={c.id} className="text-sm"><Link to={`/admin/leads?id=${c.lead_id}`} className="underline">{c.first_question || "Conversation"}</Link></li>)}</ul>
          {!d.lead_conversations.length && <p className="text-sm text-black/50">None yet.</p>}
        </section>
      </div>
    </div>
  );
}

const K_SCHEMA = [
  { key: "title", label: "Question / topic", full: true }, { key: "content", label: "Approved answer", type: "textarea", rows: 6 },
  { key: "category", label: "Category" }, { key: "status", label: "Status", type: "select", noEmpty: true, options: [{ value: "active", label: "Active (AI uses it)" }, { value: "draft", label: "Draft (Test Lab only)" }, { value: "inactive", label: "Inactive" }] },
  { key: "visibility", label: "Visibility", type: "select", noEmpty: true, options: [{ value: "public", label: "Public — can be shared with visitors" }, { value: "internal", label: "Internal guidance only" }] },
];

function Knowledge() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [hist, setHist] = useState(null);
  const [site, setSite] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/ai/knowledge").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/ai/knowledge/${edit.id}`, edit); else await api.post("/admin/ai/knowledge", edit);
      toast.success("Knowledge saved"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async (i) => { await api.post(`/admin/ai/knowledge/${hist.id}/restore/${i}`); toast.success("Previous version restored"); setHist(null); load(); };
  const del = async (k) => { if (window.confirm("Delete this knowledge entry?")) { await api.delete(`/admin/ai/knowledge/${k.id}`); load(); } };
  const shown = items.filter((k) => !q || (k.title + k.content).toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="ai-knowledge">
      <div className="mb-4 flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search knowledge…" className={`${inCls} max-w-sm`} data-testid="knowledge-search" />
        <button className={btnPrimary} onClick={() => setEdit({ status: "active", visibility: "public", category: "General" })} data-testid="knowledge-add-btn"><Plus size={14} /> Add Q&A</button>
        <button className={btnGhost} onClick={() => api.get("/admin/ai/website-knowledge").then((r) => setSite(r.data))} data-testid="knowledge-website-btn">View synced website content</button>
      </div>
      <ul className="divide-y border border-black/10 bg-white">
        {shown.map((k, i) => (
          <li key={k.id} className="flex items-start gap-3 p-4" data-testid={`knowledge-row-${i}`}>
            <div className="min-w-0 flex-1"><p className="font-semibold">{k.title}</p><p className="line-clamp-2 text-sm text-black/60">{k.content}</p>
              <p className="mt-1 text-xs text-black/40">{k.category} · source: {k.source} · {k.visibility} · updated {new Date(k.updated_at).toLocaleDateString()} by {k.updated_by}</p></div>
            <Badge s={k.status} />
            <button className={btnGhost} onClick={() => setEdit({ ...k })} data-testid={`knowledge-edit-${i}`}>Edit</button>
            {k.versions?.length > 0 && <button className="p-2 text-black/50" onClick={() => setHist(k)} aria-label="Version history" data-testid={`knowledge-history-${i}`}><History size={16} /></button>}
            <button className="p-2 text-red-600" onClick={() => del(k)} aria-label="Delete"><Trash2 size={16} /></button>
          </li>
        ))}
      </ul>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl" data-testid="knowledge-editor"><DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} knowledge entry</DialogTitle></DialogHeader>
          {edit && <Fields schema={K_SCHEMA} value={edit} onChange={setEdit} testid="knowledge" />}
          <button className={btnPrimary} onClick={save} data-testid="knowledge-save-btn">Save</button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto"><DialogHeader><DialogTitle>Version history</DialogTitle></DialogHeader>
          {hist?.versions.map((v, i) => <div key={i} className="border p-3 text-sm"><p className="font-semibold">{v.title}</p><p className="whitespace-pre-wrap text-black/70">{v.content}</p><p className="mt-1 text-xs text-black/45">{v.status} · {v.updated_at && new Date(v.updated_at).toLocaleString()} · {v.updated_by}</p><button className={`${btnGhost} mt-2`} onClick={() => restore(i)} data-testid={`restore-version-${i}`}><RotateCcw size={14} /> Restore this version</button></div>).reverse()}
        </DialogContent>
      </Dialog>
      <Dialog open={!!site} onOpenChange={(o) => !o && setSite(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto"><DialogHeader><DialogTitle>Website content the AI knows (auto-synced)</DialogTitle></DialogHeader>
          <p className="text-sm text-black/60">Everything you publish — services, projects, FAQs, process, service areas and company info — is automatically available to the assistant. Update the website and the AI updates too.</p>
          {site?.map((s, i) => <div key={i} className="border p-3 text-sm"><p className="font-semibold">{s.title}</p><p className="line-clamp-3 text-black/60">{s.content}</p></div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Documents() {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/ai/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);
  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post("/admin/ai/documents", fd); toast.success(`${data.filename}: ${data.chunks} section(s) indexed`); } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false); load();
  };
  const setStatus = async (d, status) => { await api.put(`/admin/ai/documents/${d.id}`, { status }); load(); };
  return (
    <div className="space-y-4" data-testid="ai-documents">
      <DropZone onFiles={upload} busy={busy} accept=".pdf,.docx,.txt,.md" label="Upload PDF, Word (.docx) or text files — brochures, FAQs, warranty policies, process guides" testid="doc-dropzone" />
      <p className="text-sm text-black/55">New documents start as <b>Draft</b>: test them in the Test Lab, then publish.</p>
      <ul className="divide-y border border-black/10 bg-white">
        {docs.map((d, i) => (
          <li key={d.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`doc-row-${i}`}>
            <FileUp size={18} className="text-[color:var(--ib-orange)]" />
            <div className="min-w-0 flex-1"><p className="font-semibold">{d.filename}</p><p className="line-clamp-1 text-xs text-black/50">{d.chunks} sections · {d.chars} characters · {d.text}</p></div>
            <Badge s={d.status} />
            {d.status !== "published" ? <button className={btnPrimary} onClick={() => setStatus(d, "published")} data-testid={`doc-publish-${i}`}>Publish</button> : <button className={btnGhost} onClick={() => setStatus(d, "inactive")}>Deactivate</button>}
            <button className="p-2 text-red-600" onClick={async () => { if (window.confirm("Delete document?")) { await api.delete(`/admin/ai/documents/${d.id}`); load(); } }} aria-label="Delete"><Trash2 size={16} /></button>
          </li>
        ))}
        {!docs.length && <li className="p-6 text-center text-sm text-black/50">No documents yet.</li>}
      </ul>
    </div>
  );
}

function Conversations({ onImprove }) {
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/ai/conversations", { params: { filter } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const view = (id) => api.get(`/admin/ai/conversations/${id}`).then((r) => setOpen(r.data));
  const upd = async (patch) => { await api.put(`/admin/ai/conversations/${open.id}`, patch); view(open.id); load(); };
  const flag = async (i) => { await api.post(`/admin/ai/conversations/${open.id}/messages/${i}/flag`, {}); toast.success("Response flagged as incorrect"); view(open.id); };
  const task = async () => { const text = window.prompt("Follow-up task:", `Follow up on: ${rows.find((r) => r.id === open.id)?.first_question || ""}`); if (text) { await api.post("/admin/tasks", { text, conversation_id: open.id, lead_id: open.lead_id }); toast.success("Task created"); } };
  return (
    <div data-testid="ai-conversations">
      <div className="mb-4 flex flex-wrap gap-2">{[["", "All"], ["unanswered", "Unanswered"], ["flagged", "Flagged"], ["leads", "Generated leads"], ["open", "Open"], ["resolved", "Resolved"]].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${filter === k ? "bg-[color:var(--ib-charcoal)] text-white" : "bg-white"}`} data-testid={`conv-filter-${k || "all"}`}>{l}</button>)}</div>
      <ul className="divide-y border border-black/10 bg-white">
        {rows.map((c, i) => (
          <li key={c.id}><button onClick={() => view(c.id)} className="flex w-full items-center gap-3 p-4 text-left hover:bg-orange-50/40" data-testid={`conv-row-${i}`}>
            <div className="min-w-0 flex-1"><p className="truncate font-semibold">{c.first_question || "(empty)"}</p><p className="text-xs text-black/50">{c.message_count} messages · {c.page} · {c.updated_at && new Date(c.updated_at).toLocaleString()}</p></div>
            {c.unanswered && <Badge s="unanswered" />}{c.flagged && <Badge s="flagged" />}{c.lead_id && <Badge s="lead" />}<Badge s={c.status} />
          </button></li>
        ))}
        {!rows.length && <li className="p-6 text-center text-sm text-black/50">No conversations.</li>}
      </ul>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="conversation-transcript">
          <DialogHeader><DialogTitle>Conversation transcript</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-2">
            <button className={btnGhost} onClick={() => upd({ status: open.status === "resolved" ? "open" : "resolved" })} data-testid="conv-resolve-btn"><CheckCircle2 size={14} /> {open?.status === "resolved" ? "Reopen" : "Mark resolved"}</button>
            <button className={btnGhost} onClick={task} data-testid="conv-task-btn"><ListTodo size={14} /> Create follow-up task</button>
            {open?.lead_id && <Link className={btnGhost} to={`/admin/leads?id=${open.lead_id}`}>View lead</Link>}
          </div>
          <div className="space-y-3">
            {open?.messages.map((m, i) => (
              <div key={i} className={`p-3 text-sm ${m.role === "user" ? "bg-[color:var(--ib-charcoal)] text-white" : "border bg-white"}`} data-testid={`transcript-msg-${i}`}>
                <p className="mb-1 text-[0.65rem] font-bold uppercase tracking-wider opacity-60">{m.role === "user" ? "Visitor" : "AI"} {m.unsure && "· not confident"} {m.flagged && "· flagged"} {m.improved && "· improved"}</p>
                <p className="whitespace-pre-wrap">{m.text}</p>
                {m.role === "assistant" && (
                  <div className="mt-2 flex gap-2">
                    <button className="flex items-center gap-1 text-xs font-semibold text-[color:var(--ib-orange)]" onClick={() => onImprove({ conversation_id: open.id, message_index: i, question: open.messages[i - 1]?.text, ai_answer: m.text })} data-testid={`improve-btn-${i}`}><Sparkles size={12} /> Improve AI answer</button>
                    {!m.flagged && <button className="flex items-center gap-1 text-xs text-red-600" onClick={() => flag(i)} data-testid={`flag-btn-${i}`}><Flag size={12} /> Flag incorrect</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TestLab() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [drafts, setDrafts] = useState(true);
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async () => {
    const t = input.trim(); if (!t || busy) return;
    const history = msgs.map(({ role, text }) => ({ role, text }));
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: t }, { role: "assistant", text: "" }]);
    const patch = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      await streamSSE("/admin/ai/test", { message: t, history, include_drafts: drafts }, (ev) => {
        if (ev.type === "delta") patch((l) => ({ ...l, text: l.text + ev.text }));
        if (ev.type === "done") patch((l) => ({ ...l, sources: ev.sources, unsure: ev.unsure, lead: ev.would_create_lead }));
      });
    } catch (e) { patch(() => ({ role: "assistant", text: e.message })); }
    setBusy(false);
  };
  return (
    <div className="border border-black/10 bg-white" data-testid="ai-test-lab">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
        <p className="text-sm text-black/60">Private sandbox — nothing here is saved or shown to visitors.</p>
        <label className="flex items-center gap-2 text-sm"><Switch checked={drafts} onCheckedChange={setDrafts} data-testid="test-include-drafts" /> Include draft knowledge & documents</label>
        <button className="text-sm underline" onClick={() => setMsgs([])}>Clear</button>
      </div>
      <div className="h-[420px] space-y-3 overflow-y-auto p-4">
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[85%] p-3 text-sm ${m.role === "user" ? "ml-auto bg-[color:var(--ib-charcoal)] text-white" : "bg-[color:var(--ib-off)]"}`} data-testid={`test-msg-${i}`}>
            <p className="whitespace-pre-wrap">{m.text}</p>
            {m.sources && <p className="mt-2 text-[0.7rem] text-black/50">Sources used: {m.sources.join(" · ") || "none"}{m.unsure && " · ⚠ not confident"}{m.lead && " · would submit a lead"}</p>}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t p-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} className={inCls} placeholder="Ask the assistant a question as a visitor would…" data-testid="test-input" />
        <button className={btnPrimary} disabled={busy} data-testid="test-send-btn"><Send size={14} /> Ask</button>
      </form>
    </div>
  );
}

const P_SCHEMA = [
  { key: "enabled", label: "Show assistant on website", type: "switch" }, { key: "name", label: "Assistant name" },
  { key: "welcome", label: "Welcome message", type: "textarea", rows: 3 }, { key: "tone", label: "Tone & personality", type: "textarea", rows: 2 },
  { key: "instructions", label: "Company-specific instructions", type: "textarea", rows: 5 },
  { key: "lead_questions", label: "Lead qualification questions (one per line)", type: "list", rows: 6 },
  { key: "escalation", label: "Escalation rules", type: "textarea", rows: 3 },
  { key: "starters", label: "Suggested conversation starters", type: "list", rows: 6 },
  { key: "hidden_paths", label: "Hide assistant on these pages (one path per line, e.g. /contact)", type: "list", rows: 3 },
];

function Personality() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/ai/settings").then((r) => setS(r.data)); }, []);
  const save = async () => { await api.put("/admin/ai/settings", s); toast.success("Assistant settings saved"); };
  if (!s) return <p>Loading…</p>;
  return <div className="border border-black/10 bg-white p-6" data-testid="ai-personality"><Fields schema={P_SCHEMA} value={s} onChange={setS} testid="ai" /><button className={`${btnPrimary} mt-6`} onClick={save} data-testid="ai-settings-save-btn">Save settings</button></div>;
}

function Tasks() {
  const [t, setT] = useState([]);
  const load = () => api.get("/admin/tasks").then((r) => setT(r.data));
  useEffect(() => { load(); }, []);
  return (
    <ul className="divide-y border border-black/10 bg-white" data-testid="ai-tasks">
      {t.map((x) => <li key={x.id} className="flex items-center gap-3 p-4 text-sm"><input type="checkbox" checked={x.done} onChange={async () => { await api.put(`/admin/tasks/${x.id}`, { done: !x.done }); load(); }} /><span className={x.done ? "line-through text-black/40" : ""}>{x.text}</span>{x.lead_id && <Link to={`/admin/leads?id=${x.lead_id}`} className="text-xs underline">lead</Link>}<span className="ml-auto text-xs text-black/40">{new Date(x.created_at).toLocaleDateString()}</span></li>)}
      {!t.length && <li className="p-6 text-center text-sm text-black/50">No follow-up tasks.</li>}
    </ul>
  );
}

export default function AITraining() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "opportunities";
  const [improve, setImprove] = useState(null);
  const [key, setKey] = useState(0);
  return (
    <div>
      <PageTitle title="AI Training Center" sub="Teach your Construction Concierge. It answers from approved knowledge, your documents and your published website — and never learns from visitors without your approval." />
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-black/10">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setParams({ tab: k })} className={`-mb-px whitespace-nowrap border-b-2 px-3 pb-2.5 text-sm font-semibold ${tab === k ? "border-[color:var(--ib-orange)] text-[color:var(--ib-orange)]" : "border-transparent text-black/55"}`} data-testid={`ai-tab-${k}`}>{l}</button>)}
      </div>
      <div key={key}>
        {tab === "opportunities" && <Opportunities onImprove={setImprove} />}
        {tab === "knowledge" && <Knowledge />}
        {tab === "documents" && <Documents />}
        {tab === "conversations" && <Conversations onImprove={setImprove} />}
        {tab === "test" && <TestLab />}
        {tab === "personality" && <Personality />}
        {tab === "tasks" && <Tasks />}
      </div>
      <ImproveDialog data={improve} onClose={() => setImprove(null)} onSaved={() => setKey((k) => k + 1)} />
    </div>
  );
}
